import React, { useEffect, useState } from 'react';
import { View, Pressable } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import MetaHead from '@/components/MetaHead';
import { getPageShareCard } from '@/lib/socialShareCards';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { useTheme } from '@/context/ThemeContext';
import { isDarkTheme } from '@/constants/Colors';
import {
  DocsHeader,
  DocsNavigation,
  DocsAutoNavigation,
} from '@/components/Docs/DocsComponents';
import GuidePageCopy from '@/components/Guides/GuidePageCopy';
import { addDocsVisuals } from '@/content/docsVisuals';
import { addTutorialVideoBlock } from '@/lib/tutorialVideos';
import GuidePageSkeleton from '@/components/Guides/GuidePageSkeleton';
import ArtifactLab, { supportsArtifactLab } from '@/components/Learn/ArtifactLab';
import LearnSurface from '@/components/Learn/LearnSurface';
import LearnLessonPage from '@/components/Learn/LearnLessonPage';
import { guideApi } from '@/lib/guideApi';
import {
  extractFaqsFromPageCopy,
  extractHowToStepsFromPageCopy,
  parseGuideKeywords,
} from '@/lib/guidePageSeo';

const CDN_BASE = 'https://cdn.gab.ai/';

// Dynamic, client-fetched pages — no params pre-rendered at build time.
export async function generateStaticParams() {
  return [];
}

function normalizeSlug(raw) {
  const value = Array.isArray(raw) ? raw[0] : raw;
  const s = String(value || 'index').toLowerCase();
  return s || 'index';
}

function resolveOgImage(ogImage) {
  if (!ogImage) return undefined;
  if (/^https?:\/\//i.test(ogImage)) return ogImage;
  return `${CDN_BASE}${String(ogImage).replace(/^\/+/, '')}`;
}

function GuideNotFound({ guideType }) {
  const { theme } = useTheme();
  const isDark = isDarkTheme(theme);
  const router = useRouter();
  const hubPath = guideType === 'learn' ? '/docs#learn' : '/docs';
  const hubLabel = guideType === 'learn' ? 'Browse the Learn hub' : 'Browse the docs';

  return (
    <View style={{ alignItems: 'center', paddingVertical: 64, paddingHorizontal: 24 }}>
      <View
        style={{
          width: 64,
          height: 64,
          borderRadius: 20,
          backgroundColor: isDark ? 'rgba(59,130,246,0.12)' : 'rgba(59,130,246,0.08)',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 24,
        }}
      >
        <Icon name="compass" size={28} color="#3B82F6" solid />
      </View>
      <Text
        tag="h1"
        weight="bold"
        style={{ fontSize: 32, letterSpacing: -0.5, marginBottom: 12, textAlign: 'center' }}
      >
        Page not found
      </Text>
      <Text
        tag="p"
        size="l"
        color="secondary"
        style={{ lineHeight: 28, maxWidth: 420, textAlign: 'center', marginBottom: 28 }}
      >
        We couldn&apos;t find this guide. It may have been moved or renamed — but
        everything else is right where you left it.
      </Text>
      <View style={{ flexDirection: 'row', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
        <Pressable
          onPress={() => router.push(hubPath)}
          accessibilityRole="link"
          accessibilityLabel={hubLabel}
          style={({ hovered }) => ({
            flexDirection: 'row',
            alignItems: 'center',
            gap: 8,
            paddingHorizontal: 20,
            paddingVertical: 12,
            borderRadius: 9999,
            backgroundColor: hovered ? '#2563EB' : '#3B82F6',
            transition: 'background-color 0.15s ease',
          })}
        >
          <Icon name="book-open" size={14} color="#fff" solid />
          <Text tag="span" size="m" weight="semibold" style={{ color: '#fff' }}>
            {hubLabel}
          </Text>
        </Pressable>
        <Pressable
          onPress={() => router.push('/')}
          accessibilityRole="link"
          accessibilityLabel="Back to Gab AI"
          style={({ hovered }) => ({
            flexDirection: 'row',
            alignItems: 'center',
            gap: 8,
            paddingHorizontal: 20,
            paddingVertical: 12,
            borderRadius: 9999,
            borderWidth: 1,
            borderColor: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)',
            backgroundColor: hovered
              ? isDark
                ? 'rgba(255,255,255,0.06)'
                : 'rgba(0,0,0,0.03)'
              : 'transparent',
            transition: 'background-color 0.15s ease',
          })}
        >
          <Text tag="span" size="m" weight="medium" color="secondary">
            Back to Gab AI
          </Text>
        </Pressable>
      </View>
    </View>
  );
}

export default function GuidePageScreen({ guideType = 'docs' }) {
  const { slug: rawSlug } = useLocalSearchParams();
  const slug = normalizeSlug(rawSlug);

  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setMissing(false);

    (async () => {
      const data = await guideApi.getPage(guideType, slug);
      if (cancelled) return;
      if (!data) {
        setMissing(true);
        setPage(null);
      } else {
        setPage(data);
      }
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [guideType, slug]);

  if (loading) {
    return <GuidePageSkeleton />;
  }

  if (missing || !page) {
    return (
      <GuideNotFound guideType={guideType} />
    );
  }

  const canonical =
    page.canonicalPath ||
    (guideType === 'learn'
      ? slug === 'index'
        ? '/docs#learn'
        : `/docs/learn/${slug}`
      : slug === 'index'
        ? '/docs'
        : `/docs/${slug}`);

  const guideSeo = page.guideSeo || {};
  const pageCopy = page.meta?.pageCopy || [];
  const keywordTags = parseGuideKeywords(page.meta?.keywords).slice(0, 12);
  const navPrevious = page.navigation?.previous;
  const navNext = page.navigation?.next;
  const lessonCopy = guideType === 'docs' ? addTutorialVideoBlock('docs', slug, addDocsVisuals(slug, pageCopy)) : pageCopy;

  return (
    <View>
      <MetaHead
        title={page.meta?.title || page.title}
        description={page.meta?.description || page.description || ''}
        canonical={canonical}
        keywords={page.meta?.keywords}
        image={getPageShareCard(canonical)?.image || resolveOgImage(page.meta?.ogImage)}
        type="article"
        lastModified={page.updatedAt ? new Date(page.updatedAt).toISOString() : undefined}
        article={{
          publishedTime: page.publishedAt ? new Date(page.publishedAt).toISOString() : undefined,
          modifiedTime: page.updatedAt ? new Date(page.updatedAt).toISOString() : undefined,
          author: 'Gab AI Inc.',
          section: guideSeo.articleSection || undefined,
          tags: keywordTags.length ? keywordTags : undefined,
        }}
        guide={{
          type: guideType,
          readMinutes: guideSeo.readMinutes,
          difficulty: guideSeo.difficulty,
          articleSection: guideSeo.articleSection,
          audience: guideSeo.audience,
          about: guideSeo.about,
          teaches: guideSeo.teaches,
          datePublished: page.publishedAt,
          dateModified: page.updatedAt,
          faqs: extractFaqsFromPageCopy(pageCopy),
          howToSteps: extractHowToStepsFromPageCopy(pageCopy),
          howToName: page.title,
        }}
      />

      {guideType === 'learn' ? <LearnSurface><LearnLessonPage key={slug} slug={slug} page={page} /></LearnSurface> : <>
        <DocsHeader
          badge={page.badge}
          badgeColor={page.badgeColor}
          title={page.title}
          description={page.description}
          dateModified={page.updatedAt}
          datePublished={page.publishedAt}
        />
      {supportsArtifactLab(slug) ? (
        <ArtifactLab key={slug} slug={slug} blocks={[]}>
          <GuidePageCopy blocks={lessonCopy} guideType={guideType} />
        </ArtifactLab>
      ) : <GuidePageCopy blocks={lessonCopy} guideType={guideType} />}

      {navPrevious || navNext ? (
        <DocsNavigation previous={navPrevious} next={navNext} />
      ) : (
        <DocsAutoNavigation />
      )}
      </>}
    </View>
  );
}
